import { appConfig } from "@/types/appConfig.type";

const OBFUSCATION_VERSION = 1;
const OBFUSCATION_ENABLED = appConfig.VITE_API_OBFUSCATION_ENABLED === "true";
const OBFUSCATION_KEY = appConfig.VITE_API_OBFUSCATION_KEY || "";

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

const isObfuscationEnabled = (): boolean => {
  return OBFUSCATION_ENABLED && OBFUSCATION_KEY.length > 0;
};

const getObfuscationVersion = (): string => {
  return String(OBFUSCATION_VERSION);
};

const isObfuscationEnvelope = (
  payload: unknown,
): payload is { v: number; data: string } => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return false;
  }

  const record = payload as Record<string, unknown>;
  const keys = Object.keys(record);

  return (
    keys.length === 2 &&
    keys.includes("v") &&
    keys.includes("data") &&
    record.v === OBFUSCATION_VERSION &&
    typeof record.data === "string"
  );
};

const obfuscatePayload = (payload: unknown): { v: number; data: string } => {
  const serializedPayload = JSON.stringify(payload);
  const obfuscatedData = xorBase64Encode(serializedPayload, OBFUSCATION_KEY);

  return {
    v: OBFUSCATION_VERSION,
    data: obfuscatedData,
  };
};

const deobfuscatePayload = (payload: unknown): unknown => {
  if (!isObfuscationEnvelope(payload)) {
    throw new Error("Invalid obfuscation envelope");
  }

  const decodedPayload = xorBase64Decode(payload.data, OBFUSCATION_KEY);
  return JSON.parse(decodedPayload);
};

const xorBase64Encode = (value: string, key: string): string => {
  if (!key) {
    throw new Error("Missing VITE_API_OBFUSCATION_KEY");
  }

  const valueBytes = textEncoder.encode(value);
  const keyBytes = textEncoder.encode(key);
  const xored = new Uint8Array(valueBytes.length);

  for (let index = 0; index < valueBytes.length; index += 1) {
    const valueByte: number = valueBytes[index] ?? 0;
    const keyByte: number = keyBytes[index % keyBytes.length] ?? 0;
    xored[index] = valueByte ^ keyByte;
  }

  return bytesToBase64Url(xored);
};

const xorBase64Decode = (value: string, key: string): string => {
  if (!key) {
    throw new Error("Missing VITE_API_OBFUSCATION_KEY");
  }

  const encryptedBytes = base64UrlToBytes(value);
  const keyBytes = textEncoder.encode(key);
  const decoded = new Uint8Array(encryptedBytes.length);

  for (let index = 0; index < encryptedBytes.length; index += 1) {
    const encryptedByte: number = encryptedBytes[index] ?? 0;
    const keyByte: number = keyBytes[index % keyBytes.length] ?? 0;
    decoded[index] = encryptedByte ^ keyByte;
  }

  return textDecoder.decode(decoded);
};

const bytesToBase64Url = (bytes: Uint8Array): string => {
  let binary = "";

  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }

  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
};

const base64UrlToBytes = (value: string): Uint8Array => {
  let base64 = value.replace(/-/g, "+").replace(/_/g, "/");

  while (base64.length % 4 !== 0) {
    base64 += "=";
  }

  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
};

export {
  deobfuscatePayload,
  getObfuscationVersion,
  isObfuscationEnabled,
  isObfuscationEnvelope,
  obfuscatePayload,
};
